import React, { useCallback, useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { OFFER_IMAGE_ASPECT, OFFER_IMAGE_BACKGROUND } from 'components/offers/offerImages'
import { getCroppedImg } from './utils'
import { IMAGE_SIZE_MESSAGE } from '../uploadErrors'
import { getCropLayout, OFFER_FRAME_SCALE } from './layout'

const DEFAULT_ZOOM = 2
const DEFAULT_ASPECT = 1
const RECTANGLE_ASPECT = 16 / 9

const ImageCropDialog = ({
  offerMode = false,
  allowRectangle = true,
  defaultAspect = DEFAULT_ASPECT,
  optimizeForOffer = false,
  imageSrc,
  onClose,
  onCropComplete,
  open,
  zIndex,
  disabled = false,
}) => {
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled
  const [errorMessage, setErrorMessage] = useState('')
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(offerMode ? OFFER_FRAME_SCALE : DEFAULT_ZOOM)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [editor, setEditor] = useState(null)
  const [editorSize, setEditorSize] = useState(null)
  const [sourceSize, setSourceSize] = useState(null)
  const initialAspect = offerMode ? OFFER_IMAGE_ASPECT : allowRectangle ? defaultAspect : DEFAULT_ASPECT
  const [aspect, setAspect] = useState(initialAspect)
  const layout = getCropLayout(editorSize, sourceSize?.src === imageSrc ? sourceSize : null, offerMode, aspect)
  const defaultZoom = offerMode ? OFFER_FRAME_SCALE : DEFAULT_ZOOM

  useEffect(() => {
    if (!editor) return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setEditorSize((previous) => previous?.width === width && previous?.height === height ? previous : { width, height })
    })
    observer.observe(editor)
    return () => observer.disconnect()
  }, [editor])

  useEffect(() => {
    setSourceSize(null)
    if (!open || !imageSrc) return
    const image = new Image()
    image.onload = () => setSourceSize({ src: imageSrc, width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = () => setErrorMessage('Couldn’t load this image. Choose another JPG or PNG image.')
    image.src = imageSrc
    return () => {
      image.onload = null
      image.onerror = null
    }
  }, [open, imageSrc])

  useEffect(() => {
    if (!open) return
    setErrorMessage('')
    setCrop({ x: 0, y: 0 })
    setZoom(defaultZoom)
    setAspect(initialAspect)
  }, [open, imageSrc, initialAspect, defaultZoom])

  useEffect(() => {
    setCroppedAreaPixels(null)
  }, [open, imageSrc])

  const handleClose = () => {
    setErrorMessage('')
    setCrop({ x: 0, y: 0 })
    setZoom(defaultZoom)
    setCroppedAreaPixels(null)
    setAspect(initialAspect)
    onClose()
  }

  const handleCrop = async () => {
    if (disabledRef.current || !layout || !croppedAreaPixels || !imageSrc) return
    setErrorMessage('')
    setIsProcessing(true)
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, { optimizeForOffer, background: offerMode ? OFFER_IMAGE_BACKGROUND : undefined })
      if (disabledRef.current) return
      onCropComplete(croppedImage)
      handleClose()
    } catch (error) {
      setErrorMessage(error?.code === 'IMAGE_TOO_LARGE' ? IMAGE_SIZE_MESSAGE : 'Couldn’t process this image. Choose another JPG or PNG image.')
    } finally {
      setIsProcessing(false)
    }
  }

  const onCropCompleteCallback = useCallback((croppedArea, pixels) => {
    setCroppedAreaPixels(pixels)
  }, [])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={zIndex === undefined ? undefined : { zIndex }}
      data-testid="image-crop-dialog"
    >
      <DialogTitle>Crop image</DialogTitle>
      <DialogContent>
        {errorMessage && <Typography role="alert" color="error">{errorMessage}</Typography>}
        <Box ref={setEditor} sx={{ position: 'relative', height: offerMode ? 'auto' : 400, aspectRatio: offerMode ? '4 / 3' : undefined, width: '100%', bgcolor: 'grey.100', '& .reactEasyCrop_CropArea': { border: '2px solid', borderColor: 'primary.main', boxShadow: '0 0 0 9999em rgba(0, 0, 0, 0.08)' } }}>
          {open && layout && (
            <Cropper
              key={imageSrc}
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              objectFit={layout.objectFit}
              cropSize={layout.cropSize}
              minZoom={layout.minZoom}
              restrictPosition={!offerMode}
              onCropChange={(value) => { if (!disabled) setCrop(value) }}
              onZoomChange={(value) => { if (!disabled) setZoom(value) }}
              onCropComplete={onCropCompleteCallback}
              onCropAreaChange={onCropCompleteCallback}
            />
          )}
        </Box>
        <Box sx={{ mt: 3, px: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag or zoom to adjust the image inside the frame.
            {offerMode && ' Any empty space in the saved image will be white.'}
          </Typography>
          {!offerMode && allowRectangle && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Crop shape</Typography>
              <ToggleButtonGroup
                disabled={disabled}
                value={aspect}
                exclusive
                size="small"
                onChange={(event, newAspect) => {
                  if (newAspect !== null) setAspect(newAspect)
                }}
              >
                <ToggleButton value={DEFAULT_ASPECT}>Square</ToggleButton>
                <ToggleButton value={RECTANGLE_ASPECT}>Rectangle</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography>Zoom</Typography>
              <Button
                size="small"
                variant="text"
                disabled={disabled || isProcessing}
                onClick={() => {
                  setCrop({ x: 0, y: 0 })
                  setZoom(defaultZoom)
                }}
              >
                Default
              </Button>
            </Box>
            <Slider
              disabled={disabled}
              value={zoom}
              min={layout?.minZoom ?? (offerMode ? 0.1 : 1)}
              max={3}
              step={offerMode ? 0.01 : 0.1}
              onChange={(event, value) => setZoom(value)}
              aria-label="Zoom"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">Cancel</Button>
        <Button
          onClick={handleCrop}
          variant="contained"
          color="primary"
          disabled={disabled || isProcessing || !layout || !croppedAreaPixels}
        >
          {isProcessing ? 'Processing...' : 'Crop & upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImageCropDialog
